/**
 * Mobile pill frequency selects — largest font that fits the selected label
 * with equal gap between text ↔ chevron and chevron ↔ right edge (theme CSS).
 */
(function () {
  const MOBILE = window.matchMedia("(max-width: 767.98px)");
  const MAX_PX = 15;
  const MIN_PX = 11;
  const STEP = 0.5;

  /** @type {HTMLSpanElement | null} */
  let probe = null;

  /** @returns {HTMLSpanElement} */
  function getProbe() {
    if (!probe) {
      probe = document.createElement("span");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute;left:-9999px;visibility:hidden;white-space:nowrap;pointer-events:none;";
      document.body.appendChild(probe);
    }
    return probe;
  }

  /** @type {Set<HTMLSelectElement>} */
  const registered = new Set();

  /** @param {HTMLSelectElement} select */
  function registerFrequencySelect(select) {
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.dataset.rdFrequencySelectFitBound) {
      select.dataset.rdFrequencySelectFitBound = "true";
      select.addEventListener("change", () => fitFrequencySelectFont(select));
    }
    registered.add(select);
    fitFrequencySelectFont(select);
  }

  /** @param {HTMLSelectElement} select */
  function fitFrequencySelectFont(select) {
    if (!(select instanceof HTMLSelectElement)) return;

    if (!MOBILE.matches) {
      select.style.removeProperty("font-size");
      return;
    }

    const style = getComputedStyle(select);
    const padL = parseFloat(style.paddingLeft) || 0;
    const padR = parseFloat(style.paddingRight) || 0;
    const available = select.clientWidth - padL - padR;
    if (available <= 0) return;

    const text = select.options[select.selectedIndex]?.textContent?.trim() ?? "";
    if (!text) return;

    const p = getProbe();
    p.style.fontFamily = style.fontFamily;
    p.style.fontWeight = style.fontWeight;
    p.style.fontStyle = style.fontStyle;
    p.style.letterSpacing = style.letterSpacing;
    p.textContent = text;

    let size = MIN_PX;
    for (let px = MAX_PX; px >= MIN_PX; px -= STEP) {
      p.style.fontSize = `${px}px`;
      if (p.getBoundingClientRect().width <= available) {
        size = px;
        break;
      }
    }

    select.style.setProperty("font-size", `${size}px`, "important");
  }

  /** @param {ParentNode} [root] */
  function fitAllIn(root = document) {
    registered.forEach((select) => fitFrequencySelectFont(select));
    root.querySelectorAll(".starter-pack-selling-plan-fallback__select").forEach((el) => {
      registerFrequencySelect(/** @type {HTMLSelectElement} */ (el));
    });
  }

  let raf = 0;
  /** @param {ParentNode} [root] */
  function scheduleFit(root = document) {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => fitAllIn(root));
  }

  window.RdFrequencySelectFontFit = {
    fit: fitFrequencySelectFont,
    register: registerFrequencySelect,
    fitAll: fitAllIn,
    scheduleFit,
  };

  MOBILE.addEventListener("change", () => scheduleFit());
  window.addEventListener("resize", () => scheduleFit(), { passive: true });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scheduleFit());
  } else {
    scheduleFit();
  }
})();
